import { useEffect } from 'react';
import { useEventCallback } from '../../../hooks/use-event-callback';
import { useInView } from '../../../hooks/useInView';
import { Layout } from '../../../queries/ats/layout/types';
import { classes } from '../../../utils/commonUtils';
import { PatternHandler } from '../../../views/ats-pages/PatternHandler';
import styles from './grid.module.scss';

interface BaseGridProps extends React.HTMLAttributes<HTMLUListElement> {
  className?: string;
  onScrollBottom?: () => void;
}

interface GridItemsProps extends BaseGridProps {
  items: Layout.SectionItem[];
  pattern: `${Layout.PageSectionStylePattern}`;
}

interface GridChildrenProps extends BaseGridProps {
  children: React.ReactNode;
}

type GridProps = GridItemsProps | GridChildrenProps;

export function Grid({ className, onScrollBottom, ...props }: GridProps) {
  const { ref, inView } = useInView<HTMLDivElement>({ root: null, rootMargin: '1000px', threshold: 0 });
  const bottomCallback = useEventCallback(onScrollBottom);

  useEffect(() => {
    if (inView && bottomCallback) {
      bottomCallback();
    }
  }, [inView, bottomCallback]);

  if ('items' in props) {
    const { items, pattern, ...rest } = props;
    return (
      <ul className={classes(styles.container, className)} data-pattern={pattern} {...rest}>
        {items.map((item, idx) => (
          <PatternHandler key={idx} pattern={pattern} item={item} />
        ))}
        <div ref={ref} />
      </ul>
    );
  }

  return <ul className={classes(styles.container, className)} data-pattern="wide" {...props} />;
}

interface GridSkeletonProps extends Omit<GridItemsProps, 'items'> {
  count?: number;
}

function GridSkeleton({ pattern, count = 20, className }: GridSkeletonProps) {
  return (
    <ul className={classes(styles.container, className)} data-pattern={pattern}>
      {Array.from({ length: count }).map((_, idx) => (
        <PatternHandler.Skeleton key={idx} pattern={pattern} />
      ))}
    </ul>
  );
}

Grid.Skeleton = GridSkeleton;
